import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  DollarSign, 
  TrendingUp, 
  Target, 
  Briefcase,
  Star,
  AlertTriangle,
  Flame,
  Rocket,
  Bell,
  Zap,
  UserPlus,
  PlaneTakeoff,
  Sparkles,
  Mail,
  BarChart3,
  CheckCircle,
  ArrowUpRight,
  Calendar,
  Phone,
  FileText
} from "lucide-react";
import { format, addDays } from "date-fns";

// Types
type PriorityType = 'urgent' | 'high' | 'medium' | 'low';
type ActionItemType = 'booking' | 'proposal' | 'follow-up' | 'payment' | 'call' | 'meeting';

interface RevenueMetric {
  current: number;
  target: number;
  trend: number;
  period: string;
}

interface ActionItem {
  id: number;
  title: string;
  description?: string;
  dueDate: Date;
  priority: PriorityType;
  type: ActionItemType;
  contact?: string;
  estimatedRevenue?: number;
  completed?: boolean;
}

interface Opportunity {
  id: number;
  contactName: string;
  description: string;
  estimatedValue: number;
  probability: number;
  stage: 'inquiry' | 'proposal' | 'negotiation' | 'closing';
  daysInStage: number;
}

// Mock data
const REVENUE_METRICS = {
  thisMonth: { current: 28450, target: 35000, trend: 16, period: 'This Month' },
  thisQuarter: { current: 78200, target: 100000, trend: 12, period: 'Q1 2024' },
  thisYear: { current: 78200, target: 400000, trend: 8, period: '2024' },
  pipeline: { current: 156000, target: 200000, trend: 24, period: 'Pipeline' }
};

const URGENT_ACTIONS: ActionItem[] = [
  { 
    id: 1, 
    title: 'Call Sarah Miller about Hawaii proposal', 
    description: 'Follow up on $8,500 Hawaii package',
    dueDate: new Date(), 
    contact: 'Sarah Miller',
    priority: 'urgent', 
    type: 'call',
    estimatedRevenue: 8500
  },
  { 
    id: 2, 
    title: 'Confirm Milan hotel reservation', 
    description: 'Client payment received - secure booking',
    dueDate: addDays(new Date(), 1), 
    priority: 'urgent', 
    type: 'booking',
    estimatedRevenue: 0
  },
  { 
    id: 3, 
    title: 'Send Thompson family proposal', 
    description: 'European tour for 4 - budget $15,000',
    dueDate: addDays(new Date(), 1), 
    contact: 'John Thompson',
    priority: 'high', 
    type: 'proposal',
    estimatedRevenue: 15000
  }
];

const HOT_OPPORTUNITIES: Opportunity[] = [
  {
    id: 1,
    contactName: 'Sarah Miller',
    description: 'Hawaii honeymoon package',
    estimatedValue: 8500,
    probability: 85,
    stage: 'closing',
    daysInStage: 3
  },
  {
    id: 2,
    contactName: 'Thompson Family',
    description: 'European tour package',
    estimatedValue: 15000,
    probability: 70,
    stage: 'proposal',
    daysInStage: 5
  },
  {
    id: 3,
    contactName: 'Williams Family',
    description: 'African safari adventure',
    estimatedValue: 25000,
    probability: 60,
    stage: 'negotiation',
    daysInStage: 8
  }
];

// Components
const RevenueCard: React.FC<{
  title: string;
  metric: RevenueMetric;
  icon: React.ReactNode;
  bgColor: string;
}> = ({ title, metric, icon, bgColor }) => {
  const percentage = (metric.current / metric.target) * 100;
  
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className={`${bgColor} p-4 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">{title}</p>
              <p className="text-2xl font-bold">${metric.current.toLocaleString()}</p>
            </div>
            {icon}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Target: ${metric.target.toLocaleString()}</span>
            <span className={`flex items-center ${metric.trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              <ArrowUpRight className="h-3 w-3 mr-1" />
              {metric.trend}%
            </span>
          </div>
          <Progress value={percentage} className="h-2" />
        </div>
      </CardContent>
    </Card>
  );
};

const ActionItemCard: React.FC<{
  item: ActionItem;
  onComplete: (id: number) => void;
}> = ({ item, onComplete }) => {
  const typeIcons = {
    call: <Phone className="h-4 w-4" />,
    booking: <Calendar className="h-4 w-4" />,
    proposal: <FileText className="h-4 w-4" />,
    payment: <DollarSign className="h-4 w-4" />,
    meeting: <Calendar className="h-4 w-4" />,
    'follow-up': <Bell className="h-4 w-4" />
  };

  const priorityColors = {
    urgent: 'bg-red-50 border-red-200',
    high: 'bg-orange-50 border-orange-200',
    medium: 'bg-yellow-50 border-yellow-200',
    low: 'bg-gray-50 border-gray-200'
  };

  return (
    <div className={`p-3 rounded-lg border ${priorityColors[item.priority]}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {typeIcons[item.type]}
            <span className="font-medium text-sm">{item.title}</span>
            <Badge 
              variant={item.priority === 'urgent' ? 'destructive' : 'secondary'}
              className="text-xs"
            >
              {item.priority}
            </Badge>
          </div>
          {item.description && (
            <p className="text-xs text-gray-600 mb-2">{item.description}</p>
          )}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>Due: {format(item.dueDate, 'MMM d')}</span>
            {item.contact && <span>Contact: {item.contact}</span>}
            {item.estimatedRevenue && item.estimatedRevenue > 0 && (
              <span className="text-green-600 font-medium">
                ${item.estimatedRevenue.toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onComplete(item.id)}
          className="text-xs"
        >
          Complete
        </Button>
      </div>
    </div>
  );
};

const OpportunityCard: React.FC<{
  opportunity: Opportunity;
}> = ({ opportunity }) => {
  const stageColors = {
    inquiry: 'bg-blue-100 text-blue-800',
    proposal: 'bg-yellow-100 text-yellow-800',
    negotiation: 'bg-orange-100 text-orange-800',
    closing: 'bg-green-100 text-green-800'
  };

  return (
    <Card className="h-full">
      <CardContent className="p-4">
        <div className="space-y-3">
          <div>
            <h4 className="font-medium text-sm">{opportunity.contactName}</h4>
            <p className="text-xs text-gray-600">{opportunity.description}</p>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold text-green-600">
              ${opportunity.estimatedValue.toLocaleString()}
            </span>
            <Badge className={`text-xs ${stageColors[opportunity.stage]}`}>
              {opportunity.stage}
            </Badge>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Probability</span>
              <span className="font-medium">{opportunity.probability}%</span>
            </div>
            <Progress value={opportunity.probability} className="h-1" />
            
            <p className="text-xs text-gray-500">
              {opportunity.daysInStage} days in stage
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main component
const CanvasDashboard: React.FC = () => {
  const [actions, setActions] = useState(URGENT_ACTIONS);
  
  const handleCompleteAction = (id: number) => {
    setActions(prev => prev.filter(action => action.id !== id));
  };

  const thisWeekRevenue = 51700;
  const totalPipelineValue = HOT_OPPORTUNITIES.reduce((sum, opp) => sum + opp.estimatedValue, 0);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Revenue Dashboard
        </h1>
        <p className="text-gray-600">
          Your business overview and priority actions
        </p>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <RevenueCard 
          title="Revenue This Month" 
          metric={REVENUE_METRICS.thisMonth}
          icon={<DollarSign className="h-6 w-6" />} 
          bgColor="bg-gradient-to-r from-emerald-500 to-emerald-600" 
        />
        <RevenueCard 
          title="Quarterly Progress" 
          metric={REVENUE_METRICS.thisQuarter}
          icon={<TrendingUp className="h-6 w-6" />} 
          bgColor="bg-gradient-to-r from-orange-500 to-orange-600" 
        />
        <RevenueCard 
          title="Annual Target" 
          metric={REVENUE_METRICS.thisYear}
          icon={<Target className="h-6 w-6" />} 
          bgColor="bg-gradient-to-r from-slate-500 to-slate-600" 
        />
        <RevenueCard 
          title="Pipeline Value" 
          metric={REVENUE_METRICS.pipeline}
          icon={<Briefcase className="h-6 w-6" />} 
          bgColor="bg-gradient-to-r from-slate-600 to-slate-700" 
        />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700">This Week's Potential</p>
                <p className="text-2xl font-bold text-emerald-800">
                  ${thisWeekRevenue.toLocaleString()}
                </p>
              </div>
              <Flame className="h-8 w-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-700">Hot Opportunities</p>
                <p className="text-2xl font-bold text-orange-800">
                  {HOT_OPPORTUNITIES.length}
                </p>
              </div>
              <Star className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-700">Urgent Actions</p>
                <p className="text-2xl font-bold text-slate-800">
                  {actions.filter(a => a.priority === 'urgent').length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-slate-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Action Items */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-red-500" />
                  <CardTitle>Action Required</CardTitle>
                  <Badge variant="destructive" className="text-xs">
                    {actions.length} items
                  </Badge>
                </div>
              </div>
              <CardDescription>
                High-priority actions that will directly impact your revenue
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {actions.length > 0 ? actions.map((action) => (
                <ActionItemCard 
                  key={action.id} 
                  item={action} 
                  onComplete={handleCompleteAction}
                />
              )) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">All caught up!</p>
                  <p className="text-sm">No urgent actions at the moment.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-500" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Common tasks to keep your business moving
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button className="w-full justify-start" variant="outline">
                <UserPlus className="h-4 w-4 mr-2" />
                Add New Contact
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <PlaneTakeoff className="h-4 w-4 mr-2" />
                Create New Trip
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <Sparkles className="h-4 w-4 mr-2" />
                AI Assistant
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <Mail className="h-4 w-4 mr-2" />
                Marketing Tools
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <BarChart3 className="h-4 w-4 mr-2" />
                View Reports
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hot Opportunities */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 text-blue-500" />
              <CardTitle>Hot Opportunities</CardTitle>
              <Badge className="text-xs bg-blue-100 text-blue-800">
                ${totalPipelineValue.toLocaleString()} potential
              </Badge>
            </div>
          </div>
          <CardDescription>
            High-value prospects that need immediate attention
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {HOT_OPPORTUNITIES.map((opportunity) => (
              <OpportunityCard 
                key={opportunity.id} 
                opportunity={opportunity} 
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CanvasDashboard; 